import React, { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ResponseArtifactDTO } from '@dreamai/types';
import api from '../../../api';
import {
  parseReplyFromMessage,
  extractResponseArtifactId,
} from './threadUtils';
import ResponseArtifactModal from './ResponseArtifactModal';
import './message-reply-indicator.scss';

interface ColorVariants {
  base: string;
  midDarker: string;
  darker: string;
  lighter: string;
}

interface MessageReplyIndicatorProps {
  messageContent: string | any[];
  colorVariants?: ColorVariants | null;
}

/**
 * MessageReplyIndicator
 *
 * Displays a reply indicator above user messages when they contain
 * the [* actionLine descriptionLine. Context: extraContext. *] syntax.
 *
 * If the extraContext contains a Response Artifact ID, the indicator
 * is clickable and will open a modal to view the artifact.
 */
const MessageReplyIndicator: React.FC<MessageReplyIndicatorProps> = ({
  messageContent,
  colorVariants,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [artifact, setArtifact] = useState<ResponseArtifactDTO | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract text content from message
  const textContent =
    typeof messageContent === 'string'
      ? messageContent
      : Array.isArray(messageContent)
        ? (
            messageContent.find((c) => c.type === 'text') as
              | { type: 'text'; text: string }
              | undefined
          )?.text
        : null;

  // Parse reply info from text content
  const replyInfo = textContent ? parseReplyFromMessage(textContent) : null;

  // Extract artifact ID if present
  const artifactId = replyInfo
    ? extractResponseArtifactId(replyInfo.extraContext)
    : null;
  const isClickable = !!artifactId;

  // Handle click to fetch and show artifact
  const handleClick = useCallback(async () => {
    if (!artifactId) return;

    setLoading(true);
    setError(null);
    setModalOpen(true);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<{ artifact: ResponseArtifactDTO }>(
        `/response-artifacts/artifact/${artifactId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.artifact) {
        setArtifact(response.data.artifact);
      } else {
        setError('Artifact not found');
      }
    } catch (err) {
      console.error('Failed to fetch artifact:', err);
      setError('Failed to load artifact');
    } finally {
      setLoading(false);
    }
  }, [artifactId, getAccessTokenSilently]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setArtifact(null);
    setError(null);
  }, []);

  // Don't render if no reply info
  if (!replyInfo) {
    return null;
  }

  return (
    <>
      <div
        className={`message-reply-indicator${isClickable ? ' clickable' : ''}`}
        style={colorVariants ? { color: colorVariants.darker } : undefined}
        onClick={isClickable ? handleClick : undefined}
      >
        <span className='reply-action'>{replyInfo.actionLine}</span>
        <span className='reply-description'>{replyInfo.descriptionLine}</span>
      </div>

      {/* Artifact modal with loading/error states handled internally */}
      {modalOpen && (
        <ResponseArtifactModal
          show={true}
          onClose={closeModal}
          artifacts={artifact ? [artifact] : []}
          initialIndex={0}
          colorVariants={colorVariants || undefined}
          readOnly={artifact?.status === 'actioned'}
          loading={loading}
          error={error}
        />
      )}
    </>
  );
};

export default MessageReplyIndicator;
